import React, { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Loader2, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

export interface TranscriptEditRow {
  id: string;
  title: string;
  content: string;
  summary: string;
  meeting_date: string;
  tags: string[];
  transcript_chunks_status?: string;
  transcript_chunks_indexed_at?: string | null;
}

interface TranscriptEditTabProps {
  transcript: TranscriptEditRow;
  onSaved: () => void;
  onSaveStatusChange?: (state: TranscriptEditSaveState) => void;
}

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export type TranscriptEditSaveState = {
  status: SaveStatus;
  error: string | null;
  isDirty: boolean;
};

type FormSnapshot = {
  title: string;
  meetingDate: string;
  summary: string;
  content: string;
  tags: string[];
};

const AUTO_SAVE_MS = 1200;

function meetingDateInputValue(iso: string): string {
  try {
    return new Date(iso).toISOString().split('T')[0] ?? '';
  } catch {
    return '';
  }
}

function snapshotFromTranscript(transcript: TranscriptEditRow): FormSnapshot {
  return {
    title: transcript.title,
    meetingDate: meetingDateInputValue(transcript.meeting_date),
    summary: transcript.summary ?? '',
    content: transcript.content ?? '',
    tags: transcript.tags ?? [],
  };
}

function serializeSnapshot(values: FormSnapshot): string {
  return JSON.stringify(values);
}

function indexStatusBadgeClass(status: string): string {
  switch (status) {
    case 'ready':
      return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200';
    case 'pending':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100';
    case 'error':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }
}

function indexStatusLabel(status: string): string {
  switch (status) {
    case 'ready':
      return 'Indexed';
    case 'pending':
      return 'Indexing…';
    case 'error':
      return 'Index error';
    default:
      return 'Not indexed';
  }
}

const TranscriptEditTab: React.FC<TranscriptEditTabProps> = ({ transcript, onSaved, onSaveStatusChange }) => {
  const navigate = useNavigate();
  const initial = snapshotFromTranscript(transcript);
  const [title, setTitle] = useState(initial.title);
  const [meetingDate, setMeetingDate] = useState(initial.meetingDate);
  const [summary, setSummary] = useState(initial.summary);
  const [content, setContent] = useState(initial.content);
  const [tags, setTags] = useState<string[]>(initial.tags);
  const [tagInput, setTagInput] = useState('');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexMeta, setIndexMeta] = useState({
    status: transcript.transcript_chunks_status ?? 'none',
    at: transcript.transcript_chunks_indexed_at ?? null,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const savedSnapshotRef = useRef(serializeSnapshot(initial));
  const saveInFlightRef = useRef(false);
  const savedFadeTimerRef = useRef<number | null>(null);

  const currentSnapshot = (): FormSnapshot => ({
    title,
    meetingDate,
    summary,
    content,
    tags,
  });

  const isDirty = serializeSnapshot(currentSnapshot()) !== savedSnapshotRef.current;

  useEffect(() => {
    onSaveStatusChange?.({ status: saveStatus, error: saveError, isDirty });
  }, [saveStatus, saveError, isDirty, onSaveStatusChange]);

  useEffect(() => {
    const next = snapshotFromTranscript(transcript);
    setTitle(next.title);
    setMeetingDate(next.meetingDate);
    setSummary(next.summary);
    setContent(next.content);
    setTags(next.tags);
    setIndexMeta({
      status: transcript.transcript_chunks_status ?? 'none',
      at: transcript.transcript_chunks_indexed_at ?? null,
    });
    savedSnapshotRef.current = serializeSnapshot(next);
    setSaveStatus('idle');
    setSaveError(null);
  }, [transcript.id]);

  const persist = useCallback(async () => {
    if (saveInFlightRef.current) return;

    const snap = currentSnapshot();
    const serialized = serializeSnapshot(snap);
    if (serialized === savedSnapshotRef.current) return;

    if (!snap.title.trim()) {
      setSaveStatus('error');
      setSaveError('Title is required');
      return;
    }
    if (!snap.content.trim()) {
      setSaveStatus('error');
      setSaveError('Transcript content is required');
      return;
    }

    const savedBefore = JSON.parse(savedSnapshotRef.current) as FormSnapshot;
    const contentChanged = snap.content !== savedBefore.content;

    saveInFlightRef.current = true;
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const payload: Record<string, unknown> = {
        title: snap.title.trim(),
        summary: snap.summary.trim(),
        content: snap.content.trim(),
        meeting_date: new Date(`${snap.meetingDate}T00:00:00`).toISOString(),
        tags: snap.tags,
        updated_at: new Date().toISOString(),
      };

      if (contentChanged) {
        payload.transcript_chunks_status = 'none';
        payload.transcript_chunks_indexed_at = null;
        setIndexMeta({ status: 'none', at: null });
      }

      const { error } = await supabase.from('transcripts').update(payload).eq('id', transcript.id);
      if (error) throw error;

      savedSnapshotRef.current = serialized;
      setSaveStatus('saved');
      onSaved();

      if (savedFadeTimerRef.current != null) {
        window.clearTimeout(savedFadeTimerRef.current);
      }
      savedFadeTimerRef.current = window.setTimeout(() => {
        setSaveStatus((status) => (status === 'saved' ? 'idle' : status));
      }, 2000);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to save';
      setSaveStatus('error');
      setSaveError(message);
      toast.error(message);
    } finally {
      saveInFlightRef.current = false;
    }
  }, [content, meetingDate, onSaved, summary, tags, title, transcript.id]);

  useEffect(() => {
    if (!isDirty) return;

    setSaveStatus('pending');
    setSaveError(null);
    const timer = window.setTimeout(() => {
      void persist();
    }, AUTO_SAVE_MS);

    return () => window.clearTimeout(timer);
  }, [isDirty, persist, title, meetingDate, summary, content, tags]);

  useEffect(
    () => () => {
      if (savedFadeTimerRef.current != null) {
        window.clearTimeout(savedFadeTimerRef.current);
      }
    },
    [],
  );

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags((prev) => [...prev, tag]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const handleIndexChunks = async () => {
    setIndexLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        chunkCount?: number;
        error?: string;
      }>('index-transcript-chunks', { body: { transcript_id: transcript.id } });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success(`Indexed ${data?.chunkCount ?? 0} chunk(s) for search`);
      setIndexMeta({ status: 'ready', at: new Date().toISOString() });
      onSaved();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Index failed');
      setIndexMeta((m) => ({ ...m, status: 'error' }));
    } finally {
      setIndexLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase.from('transcripts').delete().eq('id', transcript.id);
      if (error) throw error;
      toast.success('Transcript deleted');
      navigate('/transcripts');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50';

  const sectionTitleClass = 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-6 lg:gap-8 items-start">
        <div className="space-y-5 min-w-0 lg:sticky lg:top-4 lg:self-start">
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5 space-y-4">
            <h2 className={sectionTitleClass}>Meeting details</h2>

            <div>
              <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                id="edit-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label
                htmlFor="edit-meeting-date"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Meeting date
              </label>
              <input
                id="edit-meeting-date"
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label htmlFor="edit-summary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Summary
              </label>
              <textarea
                id="edit-summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={5}
                className={inputClass}
                placeholder="Brief summary of the meeting…"
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 p-5">
            <h2 className={`${sectionTitleClass} mb-3`}>Tags</h2>
            {tags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                      className="ml-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-3">No tags yet.</p>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add a tag…"
                className={`flex-1 ${inputClass}`}
              />
              <button
                type="button"
                onClick={addTag}
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shrink-0"
                aria-label="Add tag"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h2 className={sectionTitleClass}>Chat index</h2>
              <span
                className={`inline-flex shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${indexStatusBadgeClass(indexMeta.status)}`}
              >
                {indexStatusLabel(indexMeta.status)}
              </span>
            </div>
            {indexMeta.at && indexMeta.status === 'ready' ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Last indexed {new Date(indexMeta.at).toLocaleString()}
              </p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Vector chunks power Chat Q&A on this transcript.
              </p>
            )}
            <button
              type="button"
              onClick={() => void handleIndexChunks()}
              disabled={indexLoading}
              className="inline-flex w-full items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50"
            >
              {indexLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              {indexLoading ? 'Indexing…' : 'Build / refresh index'}
            </button>
          </section>

          <section className="rounded-xl border border-red-200/80 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/10 p-5">
            <h2 className={`${sectionTitleClass} text-red-600 dark:text-red-400 mb-2`}>Danger zone</h2>
            {!showDeleteConfirm ? (
              <>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Permanently remove this transcript and its linked tasks.
                </p>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex w-full items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100/80 dark:hover:bg-red-950/40"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete transcript
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                  Delete permanently? This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() => void handleDelete()}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Delete
                  </button>
                </div>
              </>
            )}
          </section>
        </div>

        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5 flex flex-col min-h-[min(88vh,56rem)] min-w-0 lg:min-h-[calc(100vh-10rem)]">
          <div className="mb-3 shrink-0">
            <h2 className={sectionTitleClass}>Full transcript</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Raw meeting text · shown read-only on the Transcript tab
            </p>
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className={`flex-1 min-h-[min(82vh,52rem)] lg:min-h-[calc(100vh-14rem)] w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono leading-relaxed resize-y focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50`}
            required
            spellCheck={false}
          />
        </section>
    </div>
  );
};

export default TranscriptEditTab;
